import React from "react";
import { Link } from "react-router-dom";
import { Download, ExternalLink } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useSite } from "../context/SiteContext";

const localize = (value, lang) => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  return value[lang] ?? value.en ?? value.ar ?? "";
};

const SmartLink = ({ to, children, className = "" }) => {
  if (!to) return null;
  const external = /^https?:\/\//i.test(to) || to.startsWith("mailto:") || to.startsWith("tel:");
  if (external) return <a href={to} target={to.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className={className}>{children}</a>;
  return <Link to={to} className={className}>{children}</Link>;
};

const SectionShell = ({ block, children }) => {
  const style = {};
  if (block.background_color) style.backgroundColor = block.background_color;
  if (block.text_color) style.color = block.text_color;
  return (
    <section className={`relative ${block.compact ? "py-10" : "py-20"} ${block.class_name || ""}`} style={style} data-cms-block={block.id || block.type}>
      <div className={block.full_width ? "w-full" : "max-w-7xl mx-auto px-6 lg:px-8"}>{children}</div>
    </section>
  );
};

const Heading = ({ block, lang }) => (
  <div className={`mb-8 ${block.align === "center" ? "text-center max-w-3xl mx-auto" : ""}`}>
    {localize(block.eyebrow, lang) && <p className="section-label mb-3">{localize(block.eyebrow, lang)}</p>}
    {localize(block.title, lang) && <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-current">{localize(block.title, lang)}</h2>}
    {localize(block.subtitle, lang) && <p className="mt-4 text-lg opacity-75 leading-relaxed">{localize(block.subtitle, lang)}</p>}
  </div>
);

const TextBody = ({ text }) => (
  <div className="space-y-4 text-base leading-8 opacity-85 whitespace-pre-line">{text}</div>
);

const CMSBlockRenderer = ({ block }) => {
  const { lang } = useLanguage();
  const { assetUrl } = useSite();
  if (!block || block.enabled === false) return null;
  const type = block.type || "text";

  if (type === "hero") {
    const bg = assetUrl(block.background_image || block.image_url);
    const video = assetUrl(block.video_url);
    return (
      <section className="relative min-h-[520px] flex items-center overflow-hidden grain" style={{ backgroundColor: block.background_color || "var(--navy-900)" }}>
        {video ? <video src={video} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" /> : bg ? <img src={bg} alt="" className="absolute inset-0 w-full h-full object-cover" /> : null}
        <div className="absolute inset-0 bg-[#07101f]/75" />
        <div className={`relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-28 w-full ${block.align === "center" ? "text-center" : ""}`}>
          {localize(block.eyebrow, lang) && <p className="section-label mb-5">{localize(block.eyebrow, lang)}</p>}
          <h1 className="font-heading text-4xl md:text-7xl font-extrabold text-white max-w-5xl">{localize(block.title, lang)}</h1>
          {localize(block.subtitle, lang) && <p className={`mt-6 text-lg md:text-xl text-slate-200 max-w-3xl leading-relaxed ${block.align === "center" ? "mx-auto" : ""}`}>{localize(block.subtitle, lang)}</p>}
          {localize(block.button_text, lang) && block.button_url && <SmartLink to={block.button_url} className="btn-gold inline-flex mt-8 px-6 py-3">{localize(block.button_text, lang)}</SmartLink>}
        </div>
      </section>
    );
  }

  if (type === "image_text") {
    const img = assetUrl(block.image_url);
    return <SectionShell block={block}><div className="grid lg:grid-cols-2 gap-12 items-center">
      <div className={block.image_side === "right" ? "lg:order-2" : ""}>{img && <img src={img} alt={localize(block.title, lang)} className="w-full max-h-[520px] object-cover" />}</div>
      <div><Heading block={block} lang={lang} /><TextBody text={localize(block.body, lang)} />{localize(block.button_text, lang) && <SmartLink to={block.button_url} className="btn-gold inline-flex mt-7 px-5 py-3">{localize(block.button_text, lang)}</SmartLink>}</div>
    </div></SectionShell>;
  }

  if (type === "video") {
    const src = assetUrl(block.video_url);
    const isEmbed = /youtube\.com|youtu\.be|vimeo\.com/i.test(src || "");
    let embed = src;
    if (/youtu\.be\//.test(src || "")) embed = `https://www.youtube.com/embed/${src.split("youtu.be/")[1].split(/[?&]/)[0]}`;
    if (/youtube\.com\/watch/.test(src || "")) { try { embed = `https://www.youtube.com/embed/${new URL(src).searchParams.get("v")}`; } catch {} }
    return <SectionShell block={block}><Heading block={block} lang={lang} /><div className="aspect-video bg-black overflow-hidden">
      {isEmbed ? <iframe src={embed} title={localize(block.title, lang) || "Video"} className="w-full h-full" allowFullScreen /> : <video src={src} controls poster={assetUrl(block.poster_url)} className="w-full h-full object-contain" />}
    </div></SectionShell>;
  }

  if (type === "gallery") {
    return <SectionShell block={block}><Heading block={block} lang={lang} /><div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {(block.items || []).map((item, i) => <figure key={item.id || i} className="bg-[#11203A] border border-white/10 overflow-hidden"><img src={assetUrl(item.image_url)} alt={localize(item.title, lang)} className="w-full h-64 object-cover" />{(localize(item.title, lang) || localize(item.body, lang)) && <figcaption className="p-4"><p className="font-semibold">{localize(item.title, lang)}</p><p className="text-sm opacity-70 mt-1">{localize(item.body, lang)}</p></figcaption>}</figure>)}
    </div></SectionShell>;
  }

  if (type === "cards") {
    return <SectionShell block={block}><Heading block={block} lang={lang} /><div className={`grid gap-5 ${block.columns === 2 ? "md:grid-cols-2" : block.columns === 4 ? "md:grid-cols-2 xl:grid-cols-4" : "md:grid-cols-3"}`}>
      {(block.items || []).map((item, i) => <div key={item.id || i} className="bg-[#11203A] border border-white/10 p-6"><h3 className="font-heading text-xl font-bold">{localize(item.title, lang)}</h3><p className="mt-3 text-sm leading-7 opacity-70 whitespace-pre-line">{localize(item.body, lang)}</p>{item.url && <SmartLink to={item.url} className="inline-flex items-center gap-2 mt-5 text-[#D4AF37] text-sm">{localize(item.link_text, lang) || (lang === "ar" ? "المزيد" : "Learn more")} <ExternalLink size={14}/></SmartLink>}</div>)}
    </div></SectionShell>;
  }

  if (type === "stats") {
    return <SectionShell block={block}><Heading block={block} lang={lang} /><div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{(block.items || []).map((item, i) => <div key={item.id || i} className="bg-[#11203A] border border-white/10 p-6 text-center"><p className="font-heading text-4xl font-extrabold text-[#D4AF37]">{item.value}</p><p className="text-sm opacity-70 mt-2">{localize(item.title, lang)}</p></div>)}</div></SectionShell>;
  }

  if (type === "files") {
    return <SectionShell block={block}><Heading block={block} lang={lang} /><div className="grid sm:grid-cols-2 gap-3">{(block.items || []).map((item, i) => <a key={item.id || i} href={assetUrl(item.url)} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-4 bg-[#11203A] border border-white/10 p-4 hover:border-[#D4AF37]"><div><p className="font-semibold">{localize(item.title, lang) || item.filename}</p>{localize(item.body, lang) && <p className="text-sm opacity-60 mt-1">{localize(item.body, lang)}</p>}</div><Download size={20} className="text-[#D4AF37] shrink-0" /></a>)}</div></SectionShell>;
  }

  if (type === "cta") {
    return <SectionShell block={{ ...block, background_color: block.background_color || "var(--navy-800)" }}><div className={`flex flex-col md:flex-row gap-7 items-center ${block.align === "center" ? "justify-center text-center" : "justify-between"}`}><div><h2 className="font-heading text-3xl md:text-4xl font-extrabold">{localize(block.title, lang)}</h2><p className="mt-3 opacity-70">{localize(block.subtitle, lang)}</p></div>{block.button_url && <SmartLink to={block.button_url} className="btn-gold px-6 py-3 shrink-0">{localize(block.button_text, lang)}</SmartLink>}</div></SectionShell>;
  }

  if (type === "spacer") return <div style={{ height: Math.max(16, Number(block.height || 64)) }} />;

  return <SectionShell block={block}><Heading block={block} lang={lang} /><TextBody text={localize(block.body, lang)} />{localize(block.button_text, lang) && block.button_url && <SmartLink to={block.button_url} className="btn-gold inline-flex mt-7 px-5 py-3">{localize(block.button_text, lang)}</SmartLink>}</SectionShell>;
};

export const CMSSections = ({ sections = [] }) => <>{sections.filter((x) => x?.enabled !== false).map((block, i) => <CMSBlockRenderer key={block.id || `${block.type}-${i}`} block={block} />)}</>;
export default CMSBlockRenderer;
